"use client";
import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

export function GraySelect({
	placeholder,
	options = [],
	onChange,
	value,
	className = "",
	isEditAble = false,
}: {
	placeholder: string;
	options: { id: number | string; title: string }[];
	onChange?: (selectedValue: string, title: string) => void;
	value?: string;
	className?: string;
	isEditAble?: boolean;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Transform incoming value to match option values format
	const formattedValue = value?.toLowerCase().replace(/\s+/g, "-");

	// Find the original title for the selected value
	const selectedOption = (options || []).find(
		(item) =>
			item.title.toLowerCase().replace(/\s+/g, "-") === formattedValue ||
			item.title.toLowerCase() === value?.toLowerCase(),
	);

	const displayTitle = selectedOption ? selectedOption.title : placeholder;

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleSelect = (item: { id: number | string; title: string }) => {
		if (isEditAble) return;
		const selectedVal = item.title.toLowerCase().replace(/\s+/g, "-");
		onChange?.(selectedVal, item.title);
		setIsOpen(false);
	};

	return (
		<div className="relative w-full" ref={dropdownRef}>
			{/* Trigger Button */}
			<button
				type="button"
				disabled={isEditAble}
				onClick={() => setIsOpen((prev) => !prev)}
				className={`flex w-full h-[38px] items-center justify-between whitespace-nowrap rounded-[8px] border border-[#A6A6A6] bg-white px-3 py-[7.5px] text-sm ff_poppins transition-colors focus:outline-none ${
					isEditAble ? "cursor-not-allowed opacity-50" : "cursor-pointer"
				} ${className}`}
			>
				<span
					className={`truncate ff_poppins capitalize ${
						selectedOption ? "text-[#021A38] font-medium" : "text-[#A6A6A6]"
					}`}
				>
					{displayTitle}
				</span>
				<ChevronDown
					className={`size-5 shrink-0 text-gray-500 transition-transform duration-200 ${
						isOpen ? "rotate-180" : ""
					}`}
				/>
			</button>

			{/* Custom Dropdown Menu */}
			{isOpen && !isEditAble && (
				<div className="absolute left-0 top-[calc(100%+4px)] z-[100] max-h-60 w-full overflow-y-auto rounded-[8px] border border-gray-200 bg-white p-1 shadow-xl ff_poppins">
					{options && options.length > 0 ? (
						options.map((item) => {
							const itemVal = item.title.toLowerCase().replace(/\s+/g, "-");
							const isSelected = itemVal === formattedValue;

							return (
								<div
									key={item.id}
									onClick={() => handleSelect(item)}
									className={`flex w-full cursor-pointer items-center rounded-[6px] px-3 py-2 text-sm capitalize ff_poppins transition-colors ${
										isSelected
											? "bg-[#1776F01A] font-semibold text-[#1776F0]"
											: "text-[#404040] hover:bg-gray-100"
									}`}
								>
									{item.title}
								</div>
							);
						})
					) : (
						<div className="px-3 py-2 text-sm text-gray-400 ff_poppins">
							No options available
						</div>
					)}
				</div>
			)}
		</div>
	);
}
